import { defineStore } from "pinia";
import { toast } from "@/controllers/alert";
import Help from "@/utils/helper";

/** Avoid importing Vuex here (circular with store modules). */
function readStoreTypeCodeFromLocalStorage() {
  try {
    const scan = localStorage.getItem("scan_store_type_code");
    if (scan) {
      return scan;
    }
    const raw = localStorage.getItem("user");
    if (!raw) {
      return null;
    }
    const u = JSON.parse(raw);
    return u?.stores?.store_type_code ?? null;
  } catch (e) {
    return null;
  }
}

function categoryIsBuffetIncluded(category, payload) {
  if (
    payload &&
    (payload.is_buffet_included === 1 ||
      payload.is_buffet_included === true)
  ) {
    return true;
  }
  const flag = category?.is_buffet_included;
  return flag === true || flag === 1;
}

/**
 * BUFFET store + product category flagged by backend (per-table filter via products_paginate).
 */
function isBuffetCartProduct(that, payload) {
  const code =
    that?.user?.stores?.store_type_code ?? readStoreTypeCodeFromLocalStorage();
  return (
    code === "BUFFET" &&
    categoryIsBuffetIncluded(payload?.category, payload)
  );
}

/** Cart line should contribute 0 to totals — only when price_sale is truly 0 (food included in plan). */
function cartLineIsBuffetFree(e) {
  if (e.isStampReward) {
    return false;
  }
  if (Number(e.item?.price_sale || 0) > 0) {
    return false; // has its own price → charged separately even inside buffet
  }
  return (
    readStoreTypeCodeFromLocalStorage() === "BUFFET" &&
    categoryIsBuffetIncluded(e.item?.category, e.item)
  );
}

export const mainStore = defineStore("counter", {
  state: () => ({
    cart: JSON.parse(localStorage.getItem("cart")) || [],
    sub_total: Number(localStorage.getItem("sub_total") || 0),
    total_discount: Number(localStorage.getItem("total_discount") || 0),
    total_price: Number(localStorage.getItem("total_price") || 0),
    received: Number(localStorage.getItem("received") || 0),
    paymentList: [
      {
        name: "cash", // both pos and restaurant
        icon: "fal fa-money-bill",
        value: "cash",
        type: "both",
      },
      {
        name: "transfer", // both pos and restaurant (align with POC)
        icon: "fal fa-exchange-alt",
        value: "transfer",
        type: "both",
      },
      // {
      //   name: "credit", // for restaurant only
      //   icon: "fal fa-credit-card",
      //   value: "credit",
      //   type: "restaurant",
      // },
      {
        name: "qr_code", // for pos only
        icon: "fal fa-qrcode",
        value: "qr",
        type: "pos",
      },
      {
        name: "cash_with_transfer", // both pos and restaurant
        icon: "fal fa-money-bill-wave",
        value: "cash_with_transfer",
        type: "both",
      },
      {
        name: "cod", // for pos only
        icon: "fal fa-truck",
        value: "cod",
        type: "pos",
      },
    ],
    // QR Payment related state
    currentBillNumber: null,
    currentPaymentId: null,
    qrPaymentTimeout: null,
    qrPaymentStartTime: null,
    manualPaymentNote: "",
    currency: null,
    // Customer Screen QR State
    customerScreenQR: {
      loading: false,
      qrData: null,
      paymentSuccess: false,
      ticketId: null,
    },
    // Customer Screen Order Meta (table click -> customer screen subtitle)
    customerScreenOrderMeta: JSON.parse(
      localStorage.getItem("customerScreenOrderMeta") || "null"
    ),
    currencySymbol: JSON.parse(localStorage.getItem("currencySymbol")) || {
      symbol: "₭",
      code: "LAK",
      exchange_rate: 1,
    },
    /** @type {Array<{buffet_plan_id:number,plan_name:string,unit_price:number,persons:number,subtotal:number,included_category_ids?:number[]}>} */
    buffet_plan_lines: JSON.parse(
      localStorage.getItem("buffet_plan_lines") || "[]"
    ),
    priceChannels: [],
    selectedPriceChannelId: JSON.parse(
      localStorage.getItem("selectedPriceChannelId") || "null"
    ),
  }),

  getters: {
    selectedPriceChannel: (state) => {
      if (!state.selectedPriceChannelId) return null;
      return (
        state.priceChannels.find(
          (c) => Number(c.id) === Number(state.selectedPriceChannelId)
        ) || null
      );
    },
    activePriceChannelName: (state) => {
      if (!state.selectedPriceChannelId) return null;
      const found = state.priceChannels.find(
        (c) => Number(c.id) === Number(state.selectedPriceChannelId)
      );
      return found ? found.name : null;
    },
    filteredPaymentList: (state) => (user) => {
      // Parse payment_methods if it's a JSON string from the database
      let paymentMethods = user?.stores?.payment_methods || {};
      if (typeof paymentMethods === "string") {
        try {
          paymentMethods = JSON.parse(paymentMethods);
        } catch (e) {
          console.error("Failed to parse payment_methods:", e);
          paymentMethods = {};
        }
      }

      const isActiveQR = user?.stores?.is_active_qr === 1;
      const isCOD = user?.stores?.is_cod === 1;
      const storeType = user?.stores?.store_type_code ?? null;

      return state.paymentList.filter((method) => {
        // Cash is always available
        if (method.value === "cash") {
          return true;
        }

        // QR payment requires both is_active_qr and payment_methods.qr
        if (method.value === "qr") {
          return (
            isActiveQR &&
            (paymentMethods.qr === true || paymentMethods.qr === 1)
          );
        }

        // COD requires both is_cod, payment_methods.cod, and POS store type
        if (method.value === "cod") {
          return (
            isCOD &&
            (paymentMethods.cod === true || paymentMethods.cod === 1) &&
            storeType === "POS"
          );
        }

        // Transfer requires payment_methods.transfer
        if (method.value === "transfer") {
          // If QR is active, transfer should be hidden/disabled as per requirements
          if (isActiveQR) {
            return false;
          }
          return (
            paymentMethods.transfer === true || paymentMethods.transfer === 1
          );
        }

        // Cash with Transfer requires payment_methods.cash_with_transfer
        if (method.value === "cash_with_transfer") {
          return (
            paymentMethods.cash_with_transfer === true ||
            paymentMethods.cash_with_transfer === 1
          );
        }

        // Default: show if enabled in payment_methods or if payment_methods is not set (backward compatibility)
        const methodKey = method.value;
        if (paymentMethods.hasOwnProperty(methodKey)) {
          return (
            paymentMethods[methodKey] === true ||
            paymentMethods[methodKey] === 1
          );
        }

        // If payment_methods is empty/not set, show all methods (backward compatibility)
        return Object.keys(paymentMethods).length === 0;
      });
    },
  },

  actions: {
    setPriceChannels(channels) {
      this.priceChannels = Array.isArray(channels) ? channels : [];
    },

    setPriceChannel(channelId) {
      const nextId = channelId ? Number(channelId) : null;
      if (this.selectedPriceChannelId === nextId) return;
      this.selectedPriceChannelId = nextId;
      if (nextId) {
        localStorage.setItem("selectedPriceChannelId", JSON.stringify(nextId));
      } else {
        localStorage.removeItem("selectedPriceChannelId");
      }

      // Update prices for existing cart items that don't have explicit customPrice
      if (Array.isArray(this.cart) && this.cart.length > 0) {
        this.cart.forEach((e) => {
          if (
            e.isStampReward ||
            cartLineIsBuffetFree(e) ||
            e.customPrice !== undefined
          ) {
            return;
          }
          const baseUnit = this.getEffectiveProductPrice(e.item, nextId);
          const uAmt =
            e.unitAmount != null ? e.unitAmount : this.getUnitMultiplier(e.item);
          e.price = baseUnit * uAmt * (e.qty || 1);
        });
        localStorage.setItem("cart", JSON.stringify(this.cart));
        this.updateCart();
      }
    },

    getEffectiveProductPrice(product, channelId = null) {
      if (!product) return 0;
      const targetChannelId =
        channelId !== null ? channelId : this.selectedPriceChannelId;
      if (targetChannelId) {
        const prices = product.channel_prices || product.channelPrices || [];
        const match = prices.find(
          (cp) =>
            Number(cp.price_channel_id) === Number(targetChannelId) &&
            cp.is_active !== 0
        );
        if (match && match.price != null && !isNaN(Number(match.price))) {
          return Number(match.price);
        }
      }
      return Number(product.price_sale || 0);
    },

    // Helper function to get the unit multiplier based on product size
    getUnitMultiplier(product) {
      // Default to 1 if no size information
      if (!product || !product.size) {
        return 1;
      }

      // If unit is "amount", multiplier is 1 (individual items)
      if (product.size.unit === "amount") {
        return 1;
      }

      // If unit is "pack", multiplier is size.amount
      if (product.size.unit === "pack" && product.size.amount) {
        return Number(product.size.amount) || 1;
      }

      // Fallback for any undefined cases
      return 1;
    },

    addToCart({
      that,
      payload,
      selectedVariationOptions = null,
      qty = 1,
      customPrice = null,
      packQty = null,
      unitAmount = null,
    }) {
      const isPackFlow = unitAmount != null && packQty != null;
      const path = that?.$route?.path || "";
      // QR Scan flow routes include params (e.g. /scan/:store_id/:branch_id/:table_id),
      // so we treat any /scan* path as a bypass for frontend stock enforcement.
      // The server will still validate on submit.
      const bypassStockCheck = typeof path === "string" && path.startsWith("/scan");
      let same = false;
      let price =
        customPrice !== null
          ? customPrice
          : this.getEffectiveProductPrice(payload);
      const unitMultiplier = this.getUnitMultiplier(payload);
      let selectedVariant = null;

      // Handle variants if product has them and variants are properly loaded
      // Now supports variants WITH pack/dynamic unit amounts
      if (
        payload.has_variations &&
        payload.variants &&
        payload.variants.length > 0 &&
        selectedVariationOptions
      ) {
        // Find the matching variant
        selectedVariant = payload.variants.find((variant) => {
          if (!variant.options) return false;
          return Object.entries(selectedVariationOptions).every(
            ([key, value]) => variant.options[key] === value
          );
        });
        if (!selectedVariant) {
          toast(that, "variation_option_not_found", "error");
          return;
        }
        // Check stock only if track_stock is enabled
        if (
          !bypassStockCheck &&
          payload.track_stock !== 0 &&
          payload.track_stock !== "0" &&
          (!selectedVariant.stock || selectedVariant.stock <= 0)
        ) {
          toast(that, "variation_option_out_of_stock", "error");
          return;
        }
        // Use variant price instead of base product price
        price = Number(selectedVariant.price) || 0;
      }

      // Calculate final price based on pack/dynamic flow
      if (isPackFlow) {
        // For pack/dynamic: price × unitAmount × packQty
        // This works for both regular products and variants (variant price already set above)
        price = price * unitAmount * packQty;
      } else {
        // For regular products: use unit multiplier
        price = price * unitMultiplier;
      }

      const buffetProduct = isBuffetCartProduct(that, payload);
      if (buffetProduct && Number(payload.price_sale || 0) === 0) {
        price = 0; // only free when price_sale is 0 (food); priced items (drinks) are charged
      }

      // Check if same product (with same variant and same custom price) already exists in cart
      this.cart.map((e) => {
        if (e.id === payload.id) {
          // For products with custom price, check if same custom price is used
          // Products with different custom prices should be treated as separate items
          if (customPrice !== null) {
            const existingCustomPrice =
              e.customPrice !== undefined ? e.customPrice : null;
            // If both have custom prices and they're different, treat as different items
            if (
              existingCustomPrice !== null &&
              existingCustomPrice !== customPrice
            ) {
              return; // Different custom price, don't merge
            }
            // If existing item has no custom price but new one does, don't merge
            if (existingCustomPrice === null && customPrice !== null) {
              return; // Different pricing, don't merge
            }
            // If existing item has custom price but new one doesn't, don't merge
            if (existingCustomPrice !== null && customPrice === null) {
              return; // Different pricing, don't merge
            }
          } else {
            // New item has no custom price, check if existing item has custom price
            if (e.customPrice !== undefined && e.customPrice !== null) {
              return; // Existing has custom price, new doesn't - don't merge
            }
          }

          // For products with variants, check if same options are selected
          if (
            payload.has_variations &&
            selectedVariant &&
            payload.variants &&
            payload.variants.length > 0
          ) {
            const sameVariations = this.compareVariationOptions(
              e.variationOptions,
              selectedVariationOptions
            );
            // For variants with pack/dynamic, also check unitAmount matches
            if (isPackFlow) {
              if ((e.unitAmount != null) !== (unitAmount != null)) return;
              if (
                e.unitAmount != null &&
                unitAmount != null &&
                e.unitAmount !== unitAmount
              )
                return;
            }
            if (sameVariations) {
              same = true;
              // Check stock only if track_stock is enabled
              if (isPackFlow) {
                // For variants with pack/dynamic, check total units needed
                const addQty = packQty;
                const uAmt = unitAmount;
                const unitsNeeded = (e.qty + addQty) * uAmt;
                const hasStock =
                  bypassStockCheck ||
                  payload.track_stock === 0 ||
                  payload.track_stock === "0" ||
                  selectedVariant.stock >= unitsNeeded;
                if (hasStock) {
                  e.qty = e.qty + addQty;
                  e.price = (selectedVariant.price || 0) * uAmt * e.qty;
                  if (e.unitAmount == null) e.unitAmount = uAmt;
                  if (buffetProduct) {
                    e.isBuffetIncluded = true;
                  }
                  if (cartLineIsBuffetFree(e)) {
                    e.price = 0;
                  }
                } else {
                  toast(that, "variation_option_stock_not_enough", "error");
                }
              } else {
                // Regular variant merge (no pack/dynamic)
                if (
                  bypassStockCheck ||
                  payload.track_stock === 0 ||
                  payload.track_stock === "0" ||
                  selectedVariant.stock >= e.qty + 1
                ) {
                  const pricePerUnit = e.price / e.qty;
                  e.qty = e.qty + 1;
                  e.price = pricePerUnit * e.qty;
                  if (buffetProduct) {
                    e.isBuffetIncluded = true;
                  }
                  if (cartLineIsBuffetFree(e)) {
                    e.price = 0;
                  }
                } else {
                  toast(that, "variation_option_stock_not_enough", "error");
                }
              }
            }
          } else {
            // Regular product without variants
            // Pack: only merge if both have same unitAmount
            if ((e.unitAmount != null) !== (unitAmount != null)) return;
            if (
              e.unitAmount != null &&
              unitAmount != null &&
              e.unitAmount !== unitAmount
            )
              return;
            same = true;
            const addQty = e.unitAmount != null ? packQty : qty;
            const uAmt = e.unitAmount || 1;
            const unitsNeeded =
              e.unitAmount != null ? (e.qty + addQty) * uAmt : e.qty + addQty;
            const hasStock =
              bypassStockCheck ||
              payload.track_stock === 0 ||
              payload.track_stock === "0" ||
              (e.unitAmount != null
                ? payload.stock >= unitsNeeded
                : payload.stock >= e.qty + addQty);
            if (hasStock) {
              e.qty = e.qty + addQty;
              e.price =
                e.unitAmount != null
                  ? (this.getEffectiveProductPrice(e.item) || 0) * e.unitAmount * e.qty
                  : (e.price / (e.qty - addQty)) * e.qty;
              if (buffetProduct) {
                e.isBuffetIncluded = true;
              }
              if (cartLineIsBuffetFree(e)) {
                e.price = 0;
              }
            } else {
              toast(that, "stock_not_enough", "error");
            }
          }
        }
      });

      if (!same) {
        let hasStock = false;
        if (bypassStockCheck || payload.track_stock === 0 || payload.track_stock === "0") {
          hasStock = true;
        } else if (isPackFlow) {
          // For pack/dynamic products, check stock based on unitAmount × packQty
          if (payload.has_variations && selectedVariant) {
            // Use variant stock for variants with pack/dynamic
            hasStock = selectedVariant.stock >= unitAmount * packQty;
          } else {
            // Use product stock for regular products with pack/dynamic
            hasStock = payload.stock >= unitAmount * packQty;
          }
        } else {
          // For regular products without pack/dynamic
          if (payload.has_variations && selectedVariant) {
            hasStock = selectedVariant.stock >= qty;
          } else if (
            payload.has_variations &&
            (!payload.variants || payload.variants.length === 0)
          ) {
            hasStock = payload.stock >= qty;
          } else {
            hasStock = payload.stock >= qty;
          }
        }

        if (hasStock) {
          const item = {
            txn: null,
            id: payload.id,
            qty: isPackFlow ? packQty : qty,
            discount: 0,
            price: isPackFlow ? price : price * qty,
            currency: payload.currency,
            exchange_rate: payload.exchange_rate,
            item: payload,
            variationOptions: selectedVariationOptions,
            variant: selectedVariant,
            variant_id:
              selectedVariant && selectedVariant.id ? selectedVariant.id : null,
            customPrice: customPrice !== null ? customPrice : undefined,
            unitMultiplier: unitMultiplier,
          };
          if (isPackFlow) item.unitAmount = unitAmount;
          if (buffetProduct) {
            item.isBuffetIncluded = true;
          }
          this.cart.unshift(item);
        } else {
          toast(
            that,
            payload.has_variations && selectedVariant
              ? "variation_option_stock_not_enough"
              : "stock_not_enough",
            "error"
          );
        }
      }

      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    // Helper method to compare variation options (plain value comparison)
    compareVariationOptions(options1, options2) {
      if (!options1 || !options2) return false;
      const keys1 = Object.keys(options1).sort();
      const keys2 = Object.keys(options2).sort();
      if (keys1.length !== keys2.length) return false;
      return keys1.every((key) => options1[key] === options2[key]);
    },

    // Helper method to get minimum stock from variation options
    getMinStockFromVariations(variationOptions) {
      if (!variationOptions) return 0;

      return Math.min(
        ...Object.values(variationOptions).map((option) => option.stock)
      );
    },

    setCart(payload) {
      // remove all cart and set new cart
      this.cart = payload;
      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    removeCart({ payload }) {
      // remove product from cart
      const index = this.cart.findIndex((item) => item.id === payload.id);
      // remove object
      this.cart.splice(index, 1);
      // update cart
      this.cart = this.cart;
      // update local storage
      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    decrementCart({ payload }) {
      // decrement product from cart
      this.cart.map((e) => {
        if (e.id === payload.id) {
          if (e.qty > 1) {
            if (cartLineIsBuffetFree(e)) {
              e.qty = e.qty - 1;
              e.price = 0;
            } else {
              const price = e.price / e.qty;
              e.qty = e.qty - 1;
              e.price = price * e.qty;
            }
          } else if (e.qty == 1) {
            this.removeCart({ payload });
          }
        }
      });
      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    /**
     * Mark a single cart line as the customer's stamp loyalty reward.
     * The line is forced to qty=1, customPrice=0 (free). If the original line
     * had qty>1, the remaining units stay on the original line at full price.
     * Only one stamp reward line is allowed at a time.
     */
    markCartLineAsStampReward(index) {
      if (index == null || index < 0 || index >= this.cart.length) return;
      // If a reward already exists, drop it first so we keep at most one.
      const existingRewardIdx = this.cart.findIndex((e) => e.isStampReward);
      if (existingRewardIdx !== -1 && existingRewardIdx !== index) {
        this.unmarkCartLineAsStampReward();
      }

      const target = this.cart[index];
      if (!target) return;

      if (target.qty > 1) {
        const remainingQty = target.qty - 1;
        const unitPrice =
          target.qty > 0 ? Number(target.price || 0) / target.qty : 0;
        target.qty = remainingQty;
        target.price = unitPrice * remainingQty;

        const rewardLine = {
          ...target,
          qty: 1,
          customPrice: 0,
          price: 0,
          isStampReward: true,
          rewardSourceId: target.id,
          rewardSourceVariantId: target.variant_id || null,
        };
        this.cart.splice(index + 1, 0, rewardLine);
      } else {
        target.customPrice = 0;
        target.price = 0;
        target.isStampReward = true;
        target.rewardSourceId = target.id;
        target.rewardSourceVariantId = target.variant_id || null;
      }

      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    /**
     * Remove the previously-applied stamp reward.
     * If the reward line was created via a split, give the unit back to a
     * compatible sibling line (same id + variant). Otherwise restore the
     * original line's price by clearing customPrice.
     */
    unmarkCartLineAsStampReward() {
      const idx = this.cart.findIndex((e) => e && e.isStampReward);
      if (idx === -1) return;

      const reward = this.cart[idx];
      const sourceId = reward.rewardSourceId ?? reward.id;
      const sourceVariantId = reward.rewardSourceVariantId ?? null;

      const siblingIdx = this.cart.findIndex(
        (e, i) =>
          i !== idx &&
          !e.isStampReward &&
          e.id === sourceId &&
          (e.variant_id || null) === sourceVariantId &&
          (e.customPrice === undefined || e.customPrice === null),
      );

      if (siblingIdx !== -1) {
        const sibling = this.cart[siblingIdx];
        const unitPrice =
          sibling.qty > 0 ? Number(sibling.price || 0) / sibling.qty : 0;
        sibling.qty = sibling.qty + 1;
        sibling.price = unitPrice * sibling.qty;
        this.cart.splice(idx, 1);
      } else {
        delete reward.customPrice;
        delete reward.isStampReward;
        delete reward.rewardSourceId;
        delete reward.rewardSourceVariantId;
        const basePrice = Number(reward.item?.price_sale || 0);
        const unitMultiplier =
          reward.unitAmount != null
            ? reward.unitAmount
            : reward.unitMultiplier || 1;
        reward.price = basePrice * unitMultiplier * reward.qty;
      }

      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    deleteCart() {
      this.cart = [];
      this.buffet_plan_lines = [];
      localStorage.setItem("cart", JSON.stringify(this.cart));
      localStorage.setItem("buffet_plan_lines", JSON.stringify([]));
      this.updateCart();
    },

    buffetPlanSubtotalSum() {
      return (this.buffet_plan_lines || []).reduce(
        (sum, line) => sum + Number(line.subtotal || 0),
        0
      );
    },

    /**
     * @returns {Array<{buffet_plan_id:number,persons:number}>}
     */
    buildBuffetPlansPayload() {
      return (this.buffet_plan_lines || []).map((line) => ({
        buffet_plan_id: Number(line.buffet_plan_id),
        persons: Math.max(1, Number(line.persons) || 1),
      }));
    },

    setBuffetPlanLines(lines) {
      this.buffet_plan_lines = Array.isArray(lines) ? lines : [];
      localStorage.setItem(
        "buffet_plan_lines",
        JSON.stringify(this.buffet_plan_lines)
      );
      return this.updateCart();
    },

    async updateCart() {
      const buffetExtra = this.buffetPlanSubtotalSum();
      if (this.cart != null && this.cart != undefined && this.cart.length > 0) {
        var total = await this.cartSummary();
        this.sub_total = total.total + buffetExtra;
        this.total_price = total.total + buffetExtra;
        this.currencySymbol = total?.currency;
        this.currency = total?.currency?.id ? total.currency : (this.currency || total?.currency || null);
      } else if (buffetExtra > 0) {
        this.sub_total = buffetExtra;
        this.total_discount = 0;
        this.received = 0;
        this.total_price = buffetExtra;
        this.currency = null;
        if (!this.currencySymbol || !this.currencySymbol.code) {
          this.currencySymbol = {
            symbol: "₭",
            code: "LAK",
            exchange_rate: 1,
          };
        }
      } else {
        this.sub_total = 0;
        this.total_discount = 0;
        this.received = 0;
        this.total_price = 0;
        this.currency = null;
        this.currencySymbol = {
          symbol: "₭",
          code: "LAK",
          exchange_rate: 1,
        };
      }
      localStorage.setItem("cart", JSON.stringify(this.cart));
      localStorage.setItem("sub_total", this.sub_total.toString());
      localStorage.setItem("total_price", this.total_price.toString());
      localStorage.setItem("total_discount", this.total_discount.toString());
      localStorage.setItem("received", this.received.toString());
      localStorage.setItem(
        "currencySymbol",
        JSON.stringify(this.currencySymbol)
      );
    },

    setQty(payload) {
      const { that, qty, id } = payload;
      this.cart.map((e) => {
        if (e.id === id) {
          let maxStock = e.item.stock;
          if (
            e.item.has_variations &&
            e.variationOptions &&
            Object.keys(e.variationOptions).length > 0
          ) {
            maxStock = this.getMinStockFromVariations(e.variationOptions);
          }
          // For pack items: max qty (packs) = floor(stock / unitAmount)
          if (e.unitAmount != null) {
            maxStock = Math.floor((e.item.stock || 0) / e.unitAmount);
          }

          if (e.item.track_stock === 0 || e.item.track_stock === "0") {
            if (qty > 0) {
              e.qty = qty;
              if (cartLineIsBuffetFree(e)) {
                e.price = 0;
              } else {
                e.price =
                  e.unitAmount != null
                    ? (this.getEffectiveProductPrice(e.item) || 0) * e.unitAmount * qty
                    : (e.price / e.qty) * qty;
              }
            }
          } else if (qty > 0 && qty <= maxStock) {
            e.qty = qty;
            if (cartLineIsBuffetFree(e)) {
              e.price = 0;
            } else {
              e.price =
                e.unitAmount != null
                  ? (this.getEffectiveProductPrice(e.item) || 0) * e.unitAmount * qty
                  : (e.price / e.qty) * qty;
            }
          } else {
            toast(
              that,
              e.item.has_variations &&
                e.variationOptions &&
                Object.keys(e.variationOptions).length > 0
                ? "variation_option_stock_not_enough"
                : "stock_not_enough",
              "error"
            );
          }
        }
      });
      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    setReceived(amount) {
      this.received = amount;
    },

    setDiscount(amount) {
      this.total_discount = amount;
      localStorage.setItem("total_discount", this.total_discount.toString());
    },

    setTotalPrice(amount) {
      this.total_price = amount;
      localStorage.setItem("total_price", this.total_price.toString());
    },

    setCurrency(currency) {
      this.currency = currency;
    },

    removeTxnCart(txn) {
      this.cart = this.cart.filter((e) => e.txn != txn);
      localStorage.setItem("cart", JSON.stringify(this.cart));
      this.updateCart();
    },

    clearCart() {
      this.cart = [];
      this.buffet_plan_lines = [];
      localStorage.setItem("buffet_plan_lines", JSON.stringify([]));
      this.sub_total = 0;
      this.total_discount = 0;
      this.total_price = 0;
      this.received = 0;
      this.payment = 1;
      localStorage.removeItem("cart");
      localStorage.removeItem("sub_total");
      localStorage.removeItem("total_discount");
      localStorage.removeItem("total_price");
      localStorage.removeItem("received");
      this.updateCart();
    },

    async resetCart(_this) {
      this.clearCart();
      const user = _this?.$store?.getters?.["auth/user"] || _this?.user;
      const hasStore = Boolean(user?.store_id || user?.stores?.id || user?.store?.id);
      if (!hasStore) {
        return;
      }
      const tableId =
        _this?.$route?.query?.tableId ?? _this?.$route?.query?.table_id;
      const url = tableId
        ? `products_paginate?table_id=${tableId}`
        : "products_paginate";
      const body = {
        that: _this,
        url,
        commit: "SET_PRODUCT",
      };
      // call the fetch action from index store
      await _this.$store.dispatch("index/fetch", body);
    },

    async saleProduct(payload) {
      const _this = payload.that;
      try {
        const form = await Help.posSale(this, payload);

        // Add shift_id to the sale form if user has an active shift
        const activeShift = _this.$store.getters["index/activeShift"];
        if (activeShift) {
          form.shift_id = activeShift.id;
        }
        if (payload.redeem_stamp_reward) {
          form.redeem_stamp_reward = true;
        }
        if (this.selectedPriceChannelId) {
          form.price_channel_id = this.selectedPriceChannelId;
        }
        // Use unified sale endpoint for all products (with and without variations)
        const { data } = await _this.$http.post("unified_sale", form);

        if (data && data.status) {
          const _data = data.data;
          // Handle QR payment waiting for payment
          if (
            _data.status === "WAITING_PAYMENT" &&
            _data.type === "qr" &&
            payload.isQRActive
          ) {
            const qrData = await this.GenerateQR(_this, _data);
            return qrData;
          }
          // For all other successful payments (cash, cash_transfer, transfer, credit, etc.), return data
          // Cart reset will be handled by the UI component to avoid double resets
          return data;
        } else {
          console.error("Sale failed:", data);
          const errorMessage = data?.message || "payment_failed";
          toast(_this, errorMessage, "error");
          return data || { status: false, message: "payment_failed" };
        }
      } catch (error) {
        console.error("saleProduct error:", error);
        return {
          status: false,
          message: error.message,
        };
      }
    },

    async GenerateQR(_this, payload) {
      try {
        const body = {
          total_price: payload.total_price,
          ticket_id: payload.code,
        };
        const { data } = await _this.$http.post("gen_qr", body);
        if (data && data.status) {
          return data;
        }
      } catch (error) {
        console.error("GenerateQR error:", error);
      }
    },

    async manualPaymentConfirmation(payload) {
      try {
      } catch (error) {
        console.error("manualPaymentConfirmation error:", error);
      }
    },

    // Customer Screen QR Actions
    setCustomerScreenQRLoading(loading) {
      this.customerScreenQR.loading = loading;
    },

    setCustomerScreenQRData(qrData) {
      this.customerScreenQR.qrData = qrData;
      this.customerScreenQR.loading = false;
      this.customerScreenQR.paymentSuccess = false;
      if (qrData && qrData.ticket_id) {
        this.customerScreenQR.ticketId = qrData.ticket_id;
      }
      localStorage.setItem(
        "customerScreenQR",
        JSON.stringify(this.customerScreenQR)
      );
    },

    setCustomerScreenPaymentSuccess(success) {
      this.customerScreenQR.paymentSuccess = success;
      if (success) {
        this.customerScreenQR.qrData = null;
        this.customerScreenQR.loading = false;
      }
      localStorage.setItem(
        "customerScreenQR",
        JSON.stringify(this.customerScreenQR)
      );
    },

    resetCustomerScreenQR() {
      this.customerScreenQR.loading = false;
      this.customerScreenQR.qrData = null;
      this.customerScreenQR.paymentSuccess = false;
      this.customerScreenQR.ticketId = null;
      localStorage.setItem(
        "customerScreenQR",
        JSON.stringify(this.customerScreenQR)
      );
    },

    // Customer Screen Order Meta Actions
    setCustomerScreenOrderMeta(meta) {
      this.customerScreenOrderMeta = meta || null;
      localStorage.setItem(
        "customerScreenOrderMeta",
        JSON.stringify(this.customerScreenOrderMeta)
      );
    },

    resetCustomerScreenOrderMeta() {
      this.customerScreenOrderMeta = null;
      localStorage.setItem("customerScreenOrderMeta", JSON.stringify(null));
    },

    async cartSummary() {
      if (!this.cart || this.cart.length === 0) {
        return {
          total: 0,
          breakdown: [],
          currency: null,
        };
      }

      // Group items by currency and exchange rate
      const currencyGroups = {};

      this.cart.forEach((item) => {
        // Ensure currency object exists and has required properties
        const currency = item.currency || { code: "LAK", symbol: "₭" };
        const currencyCode = currency.code || "LAK";
        const exchangeRate = item.exchange_rate || 1;
        const key = `${currencyCode}_${exchangeRate}`;

        if (!currencyGroups[key]) {
          currencyGroups[key] = {
            currency: currency,
            exchange_rate: exchangeRate,
            items: [],
            subtotal: 0,
          };
        }

        currencyGroups[key].items.push(item);
        currencyGroups[key].subtotal += item.price;
      });

      // Check if all items have the same currency
      const uniqueCurrencies = Object.keys(currencyGroups);
      const allSameCurrency = uniqueCurrencies.length === 1;

      const breakdown = [];

      Object.values(currencyGroups).forEach((group) => {
        const { currency, exchange_rate, subtotal } = group;

        breakdown.push({
          currency: currency?.code || "LAK",
          exchange_rate: exchange_rate,
          subtotal: subtotal,
          item_count: group.items.length,
        });
      });

      // Calculate total and determine currency based on scenario
      let total = 0;
      let currencyInfo = {};

      if (allSameCurrency) {
        // Single currency scenario
        const group = Object.values(currencyGroups)[0];
        const currencyCode = group.currency?.code || "LAK";
        const currencyId = group.currency?.id || group.items[0]?.currency_id || null;

        // Since backend now stores original prices, no conversion needed for any currency
        total = group.subtotal;
        currencyInfo = {
          id: currencyId,
          code: currencyCode,
          symbol:
            currencyCode === "THB" ? "฿" : currencyCode === "LAK" ? "₭" : "",
          exchange_rate: group.exchange_rate,
          store_exchange_rate: group.currency?.store_exchange_rate || group.exchange_rate,
        };
      } else {
        // Multiple currencies: sum all subtotals (no conversion needed)
        total = Object.values(currencyGroups).reduce(
          (sum, group) => sum + group.subtotal,
          0
        );
        currencyInfo = { code: "LAK", symbol: "₭", exchange_rate: 1 };
      }

      return {
        total: total,
        breakdown: breakdown,
        currency: currencyInfo,
      };
    },
  },
});

// Step 3: Synchronize State Across Tabs
// In your main.js or equivalent entry file, after initializing your Vue app and Pinia store:
window.addEventListener("storage", (event) => {
  if (event.key === "cart") {
    const counterStore = mainStore();
    counterStore.cart = JSON.parse(event.newValue);
  }

  if (event.key === "sub_total") {
    const counterStore = mainStore();
    counterStore.sub_total = Number(event.newValue);
  }

  if (event.key === "total_price") {
    const counterStore = mainStore();
    counterStore.total_price = Number(event.newValue);
  }

  if (event.key === "total_discount") {
    const counterStore = mainStore();
    counterStore.total_discount = Number(event.newValue);
  }

  if (event.key === "customerScreenQR") {
    const counterStore = mainStore();
    counterStore.customerScreenQR = JSON.parse(event.newValue);
  }

  if (event.key === "customerScreenOrderMeta") {
    const counterStore = mainStore();
    counterStore.customerScreenOrderMeta = JSON.parse(event.newValue);
  }

  if (event.key === "currencySymbol") {
    const counterStore = mainStore();
    counterStore.currencySymbol = JSON.parse(event.newValue);
  }

  if (event.key === "buffet_plan_lines") {
    const counterStore = mainStore();
    try {
      counterStore.buffet_plan_lines = JSON.parse(event.newValue || "[]");
    } catch (e) {
      counterStore.buffet_plan_lines = [];
    }
    void counterStore.updateCart();
  }
});
